import { api } from './api';

export interface PopupContent {
  title: string;
  content: string | null;
  contentType: 'TEXT' | 'IMAGE' | 'VIDEO';
  imageUrl: string | null;
  videoUrl: string | null;
  ctaText: string | null;
  ctaUrl: string | null;
}

export interface PopupDraft extends PopupContent {
  frequency: 'ONCE' | 'EVERY_ACCESS';
  isActive: boolean;
  priority: number;
  startDate: string | null;
  endDate: string | null;
  audienceType: 'ALL' | 'GROUPS';
  accessGroupIds: string[];
}

export interface Popup extends PopupDraft {
  id: string;
  createdAt: string;
  updatedAt: string;
  _count: { views: number };
}

export interface MemberPopupContent extends PopupContent {
  id: string;
  frequency: PopupDraft['frequency'];
}

function unwrap<T>(value: T | { data: T }): T {
  return value && typeof value === 'object' && 'data' in value
    ? value.data
    : value;
}

export const popupsService = {
  async list(): Promise<Popup[]> {
    return unwrap(
      (await api.get<Popup[] | { data: Popup[] }>('/popups/admin')).data,
    );
  },
  async create(draft: PopupDraft): Promise<Popup> {
    return unwrap(
      (
        await api.post<Popup | { data: Popup }>('/popups/admin', draft, {
          retryOnNetworkError: false,
        })
      ).data,
    );
  },
  async update(id: string, draft: Partial<PopupDraft>): Promise<Popup> {
    return unwrap(
      (
        await api.put<Popup | { data: Popup }>(`/popups/admin/${id}`, draft, {
          retryOnNetworkError: false,
        })
      ).data,
    );
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/popups/admin/${id}`, { retryOnNetworkError: false });
  },
  async active(): Promise<MemberPopupContent | null> {
    return unwrap(
      (
        await api.get<
          MemberPopupContent | null | { data: MemberPopupContent | null }
        >('/popups/active')
      ).data,
    );
  },
  async view(id: string): Promise<void> {
    await api.post(`/popups/${id}/view`, undefined, {
      retryOnNetworkError: false,
    });
  },
};
